import {
  GET_EMAIL_SENT_HISTORY_REQUEST,
  GET_EMAIL_SENT_HISTORY_SUCCESS,
  GET_EMAIL_SENT_HISTORY_FAILURE,
  EmailSentHistoryActionTypes,
  CLEAR_EMAIL_SENT_HISTORY_SUCCESS,
  GET_DRAFT_EMAILS_REQUEST,
  GET_DRAFT_EMAILS_SUCCESS,
  GET_DRAFT_EMAILS_FAILURE,
  CLEAR_DRAFT_EMAILS_SUCCESS,
  SEND_EMAIL_REQUEST,
  SEND_EMAIL_SUCCESS,
  SEND_EMAIL_FAILURE,
  SEND_ALL_EMAIL_REQUEST,
  SEND_ALL_EMAIL_SUCCESS,
  SEND_ALL_EMAIL_FAILURE,
  EXPORT_DRAFT_EMAILS_CSV_REQUEST,
  EXPORT_DRAFT_EMAILS_CSV_SUCCESS,
  EXPORT_DRAFT_EMAILS_CSV_FAILURE,
  EXPORT_SENT_EMAILS_CSV_REQUEST,
  EXPORT_SENT_EMAILS_CSV_SUCCESS,
  EXPORT_SENT_EMAILS_CSV_FAILURE,
  CLEAR_EXPORT_DRAFT_STATE,
  CLEAR_EXPORT_SENT_STATE,
  CLEAR_SEND_TEST_EMAIL_STATE,
  CLEAR_SEND_EMAIL_STATE,
  SEND_TEST_EMAIL_REQUEST,
  SEND_TEST_EMAIL_SUCCESS,
  SEND_TEST_EMAIL_FAILURE,
  GET_EMAIL_VIEW_REQUEST,
  GET_EMAIL_VIEW_SUCCESS,
  GET_EMAIL_VIEW_FAILURE,
} from './emailSentHistory.types';

export interface EmailSentHistoryState {
  history: any[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  success: boolean;
  currentCampaignId: string | null;
  filters: any;
  // Draft emails state
  draftEmails: any[];
  draftTotal: number;
  draftPage: number;
  draftLimit: number;
  draftTotalPages: number;
  draftLoading: boolean;
  draftError: string | null;
  draftSuccess: boolean;
  currentDraftCampaignId: string | null;
  draftFilters: any;
  // Send email state
  sendLoading: boolean;
  sendError: string | null;
  sendSuccess: boolean;
  sendMessage: string | null;
  sendAllLoading: boolean;
  sendAllError: string | null;
  sendAllSuccess: boolean;
  sendAllMessage: string | null;
  // Export state
  exportDraftBlob: Blob | null;
  exportDraftLoading: boolean;
  exportDraftError: string | null;
  exportDraftSuccess: boolean;
  exportSentBlob: Blob | null;
  exportSentLoading: boolean;
  exportSentError: string | null;
  exportSentSuccess: boolean;
  // Send test email state
  sendTestLoading: boolean;
  sendTestError: string | null;
  sendTestSuccess: boolean;
  sendTestMessage: string | null;
  // Email view state
  emailViewLoading: boolean;
  emailViewError: string | null;
  emailViewSuccess: boolean;
  emailViewHtmlLink: string | null;
  emailViewDetails: any | null;
}

const initialState: EmailSentHistoryState = {
  history: [],
  total: 0,
  page: 0,
  limit: 10,
  totalPages: 0,
  loading: false,
  error: null,
  success: false,
  currentCampaignId: null,
  filters: null,
  // Draft emails initial state
  draftEmails: [],
  draftTotal: 0,
  draftPage: 1,
  draftLimit: 10,
  draftTotalPages: 0,
  draftLoading: false,
  draftError: null,
  draftSuccess: false,
  currentDraftCampaignId: null,
  draftFilters: null,
  // Send email initial state
  sendLoading: false,
  sendError: null,
  sendSuccess: false,
  sendMessage: null,
  sendAllLoading: false,
  sendAllError: null,
  sendAllSuccess: false,
  sendAllMessage: null,
  // Export initial state
  exportDraftBlob: null,
  exportDraftLoading: false,
  exportDraftError: null,
  exportDraftSuccess: false,
  exportSentBlob: null,
  exportSentLoading: false,
  exportSentError: null,
  exportSentSuccess: false,
  // Send test email initial state
  sendTestLoading: false,
  sendTestError: null,
  sendTestSuccess: false,
  sendTestMessage: null,
  // Email view initial state
  emailViewLoading: false,
  emailViewError: null,
  emailViewSuccess: false,
  emailViewHtmlLink: null,
  emailViewDetails: null,
};

const emailSentHistoryReducer = (
  state: EmailSentHistoryState = initialState,
  action: EmailSentHistoryActionTypes
): EmailSentHistoryState => {
  switch (action.type) {
    case GET_EMAIL_SENT_HISTORY_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        currentCampaignId: action.payload.campaignId,
        filters: action.payload.filters || null,
      };

    case GET_EMAIL_SENT_HISTORY_SUCCESS:
      return {
        ...state,
        loading: false,
        history: action.payload.data,
        total: action.payload.pagination.total,
        page: action.payload.pagination.page,
        limit: action.payload.pagination.limit,
        totalPages: action.payload.pagination.totalPages,
        error: null,
        success: true,
      };

    case GET_EMAIL_SENT_HISTORY_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: false,
      };

    case CLEAR_EMAIL_SENT_HISTORY_SUCCESS:
      return {
        ...state,
        success: false,
      };

    case GET_DRAFT_EMAILS_REQUEST:
      return {
        ...state,
        draftLoading: true,
        draftError: null,
        currentDraftCampaignId: action.payload.campaignId,
        draftFilters: action.payload.filters || null,
      };

    case GET_DRAFT_EMAILS_SUCCESS:
      return {
        ...state,
        draftLoading: false,
        draftEmails: action.payload.data,
        draftTotal: action.payload.pagination.total,
        draftPage: action.payload.pagination.page,
        draftLimit: action.payload.pagination.limit,
        draftTotalPages: action.payload.pagination.totalPages,
        draftError: null,
        draftSuccess: true,
      };

    case GET_DRAFT_EMAILS_FAILURE:
      return {
        ...state,
        draftLoading: false,
        draftError: action.payload,
        draftSuccess: false,
      };

    case CLEAR_DRAFT_EMAILS_SUCCESS:
      return {
        ...state,
        draftSuccess: false,
      };

    case SEND_EMAIL_REQUEST:
      return {
        ...state,
        sendLoading: true,
        sendError: null,
        sendSuccess: false,
        sendMessage: null,
      };

    case SEND_EMAIL_SUCCESS:
      return {
        ...state,
        sendLoading: false,
        sendSuccess: true,
        sendMessage: action.payload.message,
        sendError: null,
      };

    case SEND_EMAIL_FAILURE:
      return {
        ...state,
        sendLoading: false,
        sendError: action.payload,
        sendSuccess: false,
        sendMessage: null,
      };

    case SEND_ALL_EMAIL_REQUEST:
      return {
        ...state,
        sendAllLoading: true,
        sendAllError: null,
        sendAllSuccess: false,
        sendAllMessage: null,
      };

    case SEND_ALL_EMAIL_SUCCESS:
      return {
        ...state,
        sendAllLoading: false,
        sendAllSuccess: true,
        sendAllMessage: action.payload.message,
        sendAllError: null,
      };

    case SEND_ALL_EMAIL_FAILURE:
      return {
        ...state,
        sendAllLoading: false,
        sendAllError: action.payload,
        sendAllSuccess: false,
        sendAllMessage: null,
      };

    case EXPORT_DRAFT_EMAILS_CSV_REQUEST:
      return {
        ...state,
        exportDraftLoading: true,
        exportDraftError: null,
        exportDraftSuccess: false,
        exportDraftBlob: null,
      };

    case EXPORT_DRAFT_EMAILS_CSV_SUCCESS:
      return {
        ...state,
        exportDraftLoading: false,
        exportDraftBlob: action.payload,
        exportDraftSuccess: true,
        exportDraftError: null,
      };

    case EXPORT_DRAFT_EMAILS_CSV_FAILURE:
      return {
        ...state,
        exportDraftLoading: false,
        exportDraftError: action.payload,
        exportDraftSuccess: false,
        exportDraftBlob: null,
      };

    case EXPORT_SENT_EMAILS_CSV_REQUEST:
      return {
        ...state,
        exportSentLoading: true,
        exportSentError: null,
        exportSentSuccess: false,
        exportSentBlob: null,
      };

    case EXPORT_SENT_EMAILS_CSV_SUCCESS:
      return {
        ...state,
        exportSentLoading: false,
        exportSentBlob: action.payload,
        exportSentSuccess: true,
        exportSentError: null,
      };

    case EXPORT_SENT_EMAILS_CSV_FAILURE:
      return {
        ...state,
        exportSentLoading: false,
        exportSentError: action.payload,
        exportSentSuccess: false,
        exportSentBlob: null,
      };

    case CLEAR_EXPORT_DRAFT_STATE:
      return {
        ...state,
        exportDraftSuccess: false,
        exportDraftBlob: null,
        exportDraftError: null,
      };

    case CLEAR_EXPORT_SENT_STATE:
      return {
        ...state,
        exportSentSuccess: false,
        exportSentBlob: null,
        exportSentError: null,
      };

    case CLEAR_SEND_TEST_EMAIL_STATE:
      return {
        ...state,
        sendTestSuccess: false,
        sendTestError: null,
        sendTestMessage: null,
      };

    case CLEAR_SEND_EMAIL_STATE:
      return {
        ...state,
        sendSuccess: false,
        sendError: null,
        sendMessage: null,
        sendAllSuccess: false,
        sendAllError: null,
        sendAllMessage: null,
      };

    case SEND_TEST_EMAIL_REQUEST:
      return {
        ...state,
        sendTestLoading: true,
        sendTestError: null,
        sendTestSuccess: false,
        sendTestMessage: null,
      };

    case SEND_TEST_EMAIL_SUCCESS:
      return {
        ...state,
        sendTestLoading: false,
        sendTestSuccess: true,
        sendTestMessage: action.payload.message,
        sendTestError: null,
      };

    case SEND_TEST_EMAIL_FAILURE:
      return {
        ...state,
        sendTestLoading: false,
        sendTestError: action.payload,
        sendTestSuccess: false,
        sendTestMessage: null,
      };

    case GET_EMAIL_VIEW_REQUEST:
      return {
        ...state,
        emailViewLoading: true,
        emailViewError: null,
        emailViewSuccess: false,
        emailViewHtmlLink: null,
        emailViewDetails: null,
      };

    case GET_EMAIL_VIEW_SUCCESS:
      return {
        ...state,
        emailViewLoading: false,
        emailViewDetails: action.payload.data,
        emailViewSuccess: true,
        emailViewError: null,
      };

    case GET_EMAIL_VIEW_FAILURE:
      return {
        ...state,
        emailViewLoading: false,
        emailViewError: action.payload,
        emailViewSuccess: false,
        emailViewHtmlLink: null,
        emailViewDetails: null,
      };

    default:
      return state;
  }
};

export default emailSentHistoryReducer;
